import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type {
  AdminUnit,
  AdminUnitsResponse,
  UnitOverrideRequest,
  UnitOverrideResponse,
  EquipmentResetRequest,
  EquipmentResetResponse,
  ResetSimulatorRequest,
  ResetSimulatorResponse,
  SetAgentTokenRequest,
  RemoteUnlockRequest,
  RemoteUnlockResponse,
  UnitEventsResponse,
  PiConnectionHealthResponse,
} from '@/api/types';

export function useAdminUnits(locationId?: string, sportCode?: string, status?: string) {
  const params = new URLSearchParams();
  if (locationId) params.set('locationId', locationId);
  if (sportCode) params.set('sportCode', sportCode);
  if (status) params.set('status', status);
  const qs = params.toString();
  return useQuery({
    queryKey: ['adminUnits', locationId, sportCode, status],
    queryFn: () => apiClient<AdminUnitsResponse>(`/admin/units${qs ? `?${qs}` : ''}`),
    refetchInterval: 30_000,
  });
}

export function useAdminUnitDetail(id?: string) {
  return useQuery({
    queryKey: ['adminUnitDetail', id],
    queryFn: () => apiClient<AdminUnit>(`/admin/units/${id}`),
    enabled: !!id,
    refetchInterval: 30_000,
  });
}

/**
 * Location-scoped Pi transport health (Sprint 10a Evening 1). A SEPARATE query with its own loading state so
 * a slow/failed pi-connection call never slows the units list — the same non-blocking rule as agent
 * reachability. Polls every 15s so a Pi that drops in the afternoon is visible in the afternoon. Enabled only
 * when a specific location is selected (there is no all-locations Pi state).
 */
export function useLocationPiHealth(locationId?: string) {
  return useQuery({
    queryKey: ['locationPiHealth', locationId],
    queryFn: () => apiClient<PiConnectionHealthResponse>(`/admin/locations/${locationId}/pi-connection`),
    enabled: !!locationId,
    refetchInterval: 15_000,
  });
}

export type UnitEventFilters = {
  eventType?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
};

export function useUnitEvents(unitId: string, filters: UnitEventFilters = {}) {
  const params = new URLSearchParams();
  if (filters.eventType) params.set('eventType', filters.eventType);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  params.set('page', String(filters.page ?? 0));
  params.set('size', String(filters.size ?? 20));
  return useQuery({
    queryKey: ['unitEvents', unitId, filters],
    queryFn: () => apiClient<UnitEventsResponse>(`/admin/units/${unitId}/events?${params.toString()}`),
    enabled: !!unitId,
  });
}

export function useOverrideUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ unitId, ...body }: UnitOverrideRequest & { unitId: string }) =>
      apiClient<UnitOverrideResponse>(`/admin/units/${unitId}/override`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminUnits'] });
      qc.invalidateQueries({ queryKey: ['adminUnitDetail'] });
      qc.invalidateQueries({ queryKey: ['unitEvents'] });
    },
  });
}

export function useResetEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ unitId, ...body }: EquipmentResetRequest & { unitId: string }) =>
      apiClient<EquipmentResetResponse>(`/admin/units/${unitId}/reset`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminUnits'] });
      qc.invalidateQueries({ queryKey: ['unitEvents'] });
    },
  });
}

/** "Reset simulator" (#93 Part B) — calls the agent's prepare on the unit's SIM_PC device (leaves power alone). */
export function useResetSimulator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ unitId, ...body }: ResetSimulatorRequest & { unitId: string }) =>
      apiClient<ResetSimulatorResponse>(`/admin/units/${unitId}/reset-simulator`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminUnitDetail'] });
      qc.invalidateQueries({ queryKey: ['unitEvents'] });
    },
  });
}

/**
 * #100 — SET (not mint) a unit's Windows-control-agent token. The agent generates its own token on first
 * run; the operator copies the plaintext and the platform stores it encrypted. Mirrors useResetSimulator.
 * On success, refetch the unit so the "token set" indicator flips to set. The token value is never returned.
 */
export function useSetAgentToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ unitId, ...body }: SetAgentTokenRequest & { unitId: string }) =>
      apiClient<AdminUnit>(`/admin/units/${unitId}/agent-token`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminUnitDetail'] });
      qc.invalidateQueries({ queryKey: ['unitEvents'] });
    },
  });
}

export function useUnlockUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ unitId, ...body }: RemoteUnlockRequest & { unitId: string }) =>
      apiClient<RemoteUnlockResponse>(`/admin/units/${unitId}/unlock`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unitEvents'] });
    },
  });
}

/**
 * Restore a Layer-4-suspended unit (OFFLINE→ONLINE + SUSPENSION_RESTORED watermark). Shared by the
 * units detail page (#88) and the incident dashboard (#144) — one hook, invalidates every surface
 * that shows unit status so the restore is reflected in both.
 */
export function useRestoreUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (unitId: string) =>
      apiClient<AdminUnit>(`/admin/units/${unitId}/restore-from-suspension`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminUnits'] });
      qc.invalidateQueries({ queryKey: ['adminUnitDetail'] });
      qc.invalidateQueries({ queryKey: ['unitEvents'] });
      qc.invalidateQueries({ queryKey: ['adminIncidents'] });
    },
  });
}

export function useUnlockLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, ...body }: RemoteUnlockRequest & { locationId: string }) =>
      apiClient<RemoteUnlockResponse>(`/admin/locations/${locationId}/unlock`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unitEvents'] });
    },
  });
}
